// src/components/auth/AuthContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import http from "../../lib/http";
import { DEFAULT_CLINIC_ID, DEV_SKIP_AUTH } from "../../lib/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(true);

  // idle timeout setup
  const IDLE_TIMEOUT = 15 * 60 * 1000;
  const idleTimerRef = useRef(null);
  const userRef = useRef(null);
  const sessionChannel = useRef(null);
  const currentSessionId = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Define clearIdleTimer early so it can be used in other hooks
  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  // Ensure default clinic_id exists for interceptors / consumers from the very start
  // Also clear any token cookies to avoid conflicts with clinic-side
  useEffect(() => {
    try {
      if (!localStorage.getItem("clinic_id")) {
        localStorage.setItem("clinic_id", DEFAULT_CLINIC_ID || "dev-clinic");
      }
      // Clear token cookie to avoid conflicts with other apps on same domain
      // We use localStorage + Authorization header instead
      document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

      // Set up BroadcastChannel for cross-tab session management
      // Only one session per user should be active across all tabs
      if (typeof BroadcastChannel !== "undefined") {
        sessionChannel.current = new BroadcastChannel("practitioners_auth");
      }
    } catch {
      // ignore storage errors (private mode, etc.)
    }

    return () => {
      if (sessionChannel.current) {
        sessionChannel.current.close();
      }
    };
  }, []);

  // Set up BroadcastChannel message handler (separate effect to handle logout dependency)
  useEffect(() => {
    if (sessionChannel.current) {
      sessionChannel.current.onmessage = (event) => {
        console.log("BroadcastChannel message received:", event.data);
        console.log("Current session ID:", currentSessionId.current);

        if (event.data.type === "NEW_LOGIN" && event.data.sessionId !== currentSessionId.current) {
          console.log("Logging out this tab due to new login in another tab");
          // Another tab logged in, force logout this tab
          toast.error("You have been logged out because you logged in from another tab");
          // Clear all auth data immediately
          localStorage.removeItem("token");
          localStorage.removeItem("therapist_id");
          localStorage.removeItem("clinic_id");
          delete http.defaults.headers.common.Authorization;
          document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
          clearIdleTimer();
          setUser(null);
          setProfileComplete(true);
          currentSessionId.current = null;
        } else if (event.data.type === "LOGOUT") {
          console.log("Logging out this tab due to logout in another tab");
          // Another tab logged out, sync this tab
          localStorage.removeItem("token");
          localStorage.removeItem("therapist_id");
          localStorage.removeItem("clinic_id");
          delete http.defaults.headers.common.Authorization;
          document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
          clearIdleTimer();
          setUser(null);
          setProfileComplete(true);
          currentSessionId.current = null;
        }
      };
    }
  }, [clearIdleTimer]);

  const checkProfileCompletion = useCallback(async (therapistId) => {
    try {
      const res = await http.get(`/therapists/${therapistId}`);
      const therapist = res?.data?.therapist || res?.data;

      // Check if essential profile fields are completed
      const isComplete = Boolean(
        therapist?.fullName &&
        therapist?.email &&
        therapist?.phone &&
        therapist?.profilePhotoUrl &&
        therapist?.bio &&
        therapist?.massageTypes?.length > 0 &&
        therapist?.crmtaCertificateNumber &&
        therapist?.crmtaCertificateExpiry &&
        therapist?.personaClientAxes?.comfortVibe &&
        therapist?.personaClientAxes?.therapistEnergy
      );

      setProfileComplete(isComplete);
      return isComplete;
    } catch (error) {
      console.error("Failed to check profile completion:", error);
      setProfileComplete(true); // Default to true to avoid blocking on error
      return true;
    }
  }, []);

  const logout = useCallback(() => {
    // clear all auth-related data
    localStorage.removeItem("token");
    localStorage.removeItem("therapist_id");
    localStorage.removeItem("clinic_id");
    delete http.defaults.headers.common.Authorization;
    // Clear token cookie if it exists
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    clearIdleTimer();
    setUser(null);
    setProfileComplete(true);
    currentSessionId.current = null;

    // Notify other tabs about logout
    if (sessionChannel.current) {
      try {
        sessionChannel.current.postMessage({ type: "LOGOUT" });
      } catch (e) {
        console.error("Failed to broadcast logout:", e);
      }
    }
  }, [clearIdleTimer]);

  const resetIdleTimer = useCallback(() => {
    clearIdleTimer();
    if (userRef.current) {
      idleTimerRef.current = setTimeout(() => {
        logout();
        toast.error("Session expired due to inactivity");
      }, IDLE_TIMEOUT);
    }
  }, [clearIdleTimer, logout]);

  const applyDevSession = useCallback(() => {
    const clinicId =
      localStorage.getItem("clinic_id") || DEFAULT_CLINIC_ID || "dev-clinic";
    const therapistId = "dev-therapist-id"; // Use a dev-specific ID instead of static

    const payload = {
      token: "dev-token",
      therapist_id: therapistId,
      clinic_id: clinicId,
      role: "developer",
      email: "dev@local.test",
    };

    localStorage.setItem("token", payload.token);
    localStorage.setItem("therapist_id", therapistId);
    if (clinicId) localStorage.setItem("clinic_id", clinicId);
    http.defaults.headers.common.Authorization = `Bearer ${payload.token}`;
    setUser(payload);
    // arm idle timer after we have a user
    setTimeout(() => resetIdleTimer(), 0);
    return payload;
  }, [resetIdleTimer]);

  const login = useCallback(
    async (email, password, clinicId) => {
      try {
        const res = await http.post("/auth/therapist/login", {
          email,
          password,
          clinicId,
        });
        const { token, user: u } = res.data;

        // Get therapist ID from the response, with fallbacks
        const therapistId =
          u?.therapistId ||
          u?.therapist_id ||
          u?.id ||
          localStorage.getItem("therapist_id");

        // Use provided clinicId or get from response, with fallbacks
        const finalClinicId =
          clinicId ||
          u?.clinicId ||
          u?.clinic_id ||
          localStorage.getItem("clinic_id") ||
          DEFAULT_CLINIC_ID ||
          "dev-clinic";

        if (!therapistId) {
          throw new Error("No therapist ID received from server");
        }

        localStorage.setItem("token", token);
        localStorage.setItem("therapist_id", therapistId);
        localStorage.setItem("clinic_id", finalClinicId);

        http.defaults.headers.common.Authorization = `Bearer ${token}`;

        setUser({
          token,
          therapist_id: therapistId,
          clinic_id: finalClinicId,
          role: u?.role || null,
          email: u?.email || email,
        });
        resetIdleTimer();

        // Generate unique session ID and notify other tabs
        currentSessionId.current = Date.now() + "_" + Math.random();
        console.log("New login - Session ID:", currentSessionId.current);
        console.log("BroadcastChannel exists:", !!sessionChannel.current);

        if (sessionChannel.current) {
          try {
            const message = {
              type: "NEW_LOGIN",
              sessionId: currentSessionId.current
            };
            console.log("Broadcasting NEW_LOGIN message:", message);
            sessionChannel.current.postMessage(message);
            console.log("Message broadcast successful");
          } catch (e) {
            console.error("Failed to broadcast login:", e);
          }
        } else {
          console.warn("BroadcastChannel not available, skipping broadcast");
        }

        // Check profile completion after successful login
        await checkProfileCompletion(therapistId);

        return { success: true };
      } catch (error) {
        if (DEV_SKIP_AUTH) {
          applyDevSession();
          return { success: true, devBypass: true };
        }
        return {
          success: false,
          error:
            error?.response?.data?.detail || error?.message || "Login failed",
        };
      }
    },
    [applyDevSession, checkProfileCompletion, resetIdleTimer]
  );

  const checkAuth = useCallback(async () => {
    if (DEV_SKIP_AUTH) {
      applyDevSession();
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("token");
    const therapistId = localStorage.getItem("therapist_id");

    if (token && therapistId) {
      try {
        http.defaults.headers.common.Authorization = `Bearer ${token}`;
        const res = await http.get("/auth/me");

        // Get therapist ID from the response, with fallback to stored value
        const responseTherapistId =
          res?.data?.user?.therapistId ||
          res?.data?.user?.therapist_id ||
          res?.data?.user?.id ||
          therapistId;

        // Get clinic ID from the response, with fallbacks
        const clinicId =
          res?.data?.user?.clinicId ||
          res?.data?.user?.clinic_id ||
          localStorage.getItem("clinic_id") ||
          DEFAULT_CLINIC_ID ||
          "dev-clinic";

        // Update stored IDs if they changed
        if (responseTherapistId !== therapistId) {
          localStorage.setItem("therapist_id", responseTherapistId);
        }
        localStorage.setItem("clinic_id", clinicId);

        setUser({
          token,
          therapist_id: responseTherapistId,
          clinic_id: clinicId,
          role: res?.data?.user?.role || null,
          email: res?.data?.user?.email || null,
        });
        resetIdleTimer();

        // Check profile completion
        await checkProfileCompletion(responseTherapistId);
      } catch {
        // token invalid: clear all auth data
        logout();
      }
    } else {
      // No token or therapist ID - user needs to login
      setUser(null);
    }
    setLoading(false);
  }, [applyDevSession, logout, resetIdleTimer, checkProfileCompletion]);

  // mount effect: auth check + global idle listeners
  useEffect(() => {
    checkAuth();
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];
    const handler = () => resetIdleTimer();
    events.forEach((e) => document.addEventListener(e, handler, true));
    return () => {
      events.forEach((e) => document.removeEventListener(e, handler, true));
      clearIdleTimer();
    };
  }, [checkAuth, clearIdleTimer, resetIdleTimer]);

  // kick the timer whenever the callback identity changes (stable now)
  useEffect(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  // always expose a stable therapistId to consumers
  const therapistId = user?.therapist_id;

  return (
    <AuthContext.Provider
      value={{
        user,
        therapistId,
        login,
        logout,
        loading,
        profileComplete,
        checkProfileCompletion,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
